import { c as _c } from "react/compiler-runtime";
import { useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import classNames from 'classnames';
import type { ShippingCosts } from 'server/detail-requests/ShippingCost';
import { OptimizelySubitoContext, userIdPrefix } from '@sbt-web/houston-wrapper';
import { Usefullinks } from '@sbt-web/ancillary-services';
import { SpecialCampaignBanner, type SpecialCampaignBannerProps } from '@sbt-web/banners';
import { useViewport } from '@sbt-web/hooks';
import { AdItem, AdTypes, CategoryID, FeatureURI, type ItemGeo } from '@sbt-web/network/types';
import { ItemInsertionDate, ItemPrice, Notice } from '@sbt-web/ui';
import { isShippable } from '@sbt-web/utils';
import useUser from '@/client/contexts/UserContext';
import parsePrice from '@/client/utilities/parse-price';
import { prepareRemoteDataAdDetail } from '@/shared/helpers/Hebe';
import { isPro } from '@/shared/models/Advertiser';
import { getOrCreatePulse } from '@/tools/tracking/utils';
import { getTransactionFlows, isFaceToFaceAllowed, isPaypalEnabled, TransactionFlow } from '@/tools/transaction/utils';
import { useAdvToggle } from '@/client/hooks/useAdvToggle';
import { useAncillaryServicesStatus } from '@/client/hooks/useAncillaryServicesStatus';
import { AdBuyEvent } from '../AdBuy';
import { BuyNowButton } from '../AdBuy/components/AdBuyButtons/BuyNowButton';
import { SendOfferButton } from '../AdBuy/components/AdBuyButtons/SendOfferButton';
import { Face2FaceBox } from '../Face2FaceBox';
import { CertyWidget } from '../Certy';
import { InfoBox } from '../InfoBox';
import { PayPalMessage } from '../PayPalMessage';
import ownClasses from './index.module.scss';
export type AdInfoProps = {
  item: AdItem;
  FavoriteCounter: ReactNode | null;
  shippingCosts: ShippingCosts | null;
  specialCampaignBannerData?: SpecialCampaignBannerProps | null;
};
const parseId = (adUrn: string): string => adUrn.split(':')[4];
const PAYPAL_3_in_1_MIN_PRICE = 30;
const Location = t0 => {
  const $ = _c(7);
  const {
    geoData
  } = t0;
  const {
    city,
    town
  } = geoData;
  const townString = town?.value ?? "";
  const cityString = city?.shortName ? `(${city.shortName})` : "";
  let t1;
  if ($[0] !== cityString || $[1] !== townString) {
    t1 = townString || cityString ? `${townString} ${cityString}`.trim() : null;
    $[0] = cityString;
    $[1] = townString;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const locationString = t1;
  if (!locationString) {
    return null;
  }
  let t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <img src={`${process.env.NEXT_PUBLIC_ASSETS_BASE_URL}/static/icons/cactus/pin.svg`} alt="" height="16" width="16" decoding="async" />;
    t3 = classNames("body-text", "book", "small", ownClasses.locationText);
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  let t4;
  if ($[5] !== locationString) {
    t4 = <div className={ownClasses.location}>{t2}<p className={t3}>{locationString}</p></div>;
    $[5] = locationString;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
};
const statusMessageMap = new Map<string, string>([['IN_PROGRESS', "Il venditore ha già accettato un'offerta per questo articolo"], ['SOLD', 'Il venditore ha concluso la trattativa con TuttoSubito']]);
const regionsCertyWidget = ['1', '5', '19', '18', '16', '20', '4'];
const shouldRenderCertyWidget = (item: AdItem): boolean => regionsCertyWidget.includes(item.geo.region.id) && item.category.id === CategoryID.CaseVacanza;
const AdInfo = t0 => {
  const $ = _c(87);
  const {
    item,
    FavoriteCounter,
    shippingCosts,
    specialCampaignBannerData
  } = t0;
  const parsedPrice = parsePrice(item.features[FeatureURI.Price]);
  const {
    optimizely
  } = useContext(OptimizelySubitoContext);
  const [showFace2Face, setShowFace2Face] = useState(false);
  const {
    noAdvToggle,
    optimizelyReady
  } = useAdvToggle();
  const ancillaryServicesEnabled = useAncillaryServicesStatus(item);
  const {
    isMobile,
    isDesktop
  } = useViewport();
  let t1;
  if ($[0] !== item) {
    t1 = isShippable(item);
    $[0] = item;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isShippableAd = t1;
  const transactionStatus = item.features[FeatureURI.TransactionStatus]?.values[0].value;
  const isSold = transactionStatus != undefined;
  const {
    user
  } = useUser();
  let t2;
  if ($[2] !== item) {
    t2 = isPaypalEnabled(item);
    $[2] = item;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const showPaypal = t2;
  const t3 = user?.id;
  let t4;
  if ($[4] !== item || $[5] !== t3) {
    t4 = getTransactionFlows(item, t3);
    $[4] = item;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const transactionFlows = t4;
  let t5;
  if ($[7] !== transactionFlows) {
    t5 = transactionFlows.includes(TransactionFlow.BuyNow);
    $[7] = transactionFlows;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const isBuyNowVisibleMemo = t5;
  let t6;
  if ($[9] !== isBuyNowVisibleMemo || $[10] !== transactionFlows) {
    t6 = !isBuyNowVisibleMemo && transactionFlows.includes(TransactionFlow.SendOffer);
    $[9] = isBuyNowVisibleMemo;
    $[10] = transactionFlows;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const isSendOfferVisibleMemo = t6;
  const isAdEligibleFor3in1 = showPaypal && !!parsedPrice && parsedPrice >= PAYPAL_3_in_1_MIN_PRICE;
  const openBuyNowFlow = _temp;
  const openSendOfferFlow = _temp2;
  let t7;
  let t8;
  if ($[12] !== item || $[13] !== optimizely || $[14] !== user) {
    t7 = () => {
      if (user && isFaceToFaceAllowed(item, user.id) && optimizely) {
        optimizely.onReady().then(() => {
          const f2ftest = optimizely.activate("subito-web-face2face", `${userIdPrefix}${user.id}`, {
            urn: item.urn
          });
          if (f2ftest === "B") {
            setShowFace2Face(true);
          }
        });
      }
    };
    t8 = [user, item, optimizely];
    $[12] = item;
    $[13] = optimizely;
    $[14] = user;
    $[15] = t7;
    $[16] = t8;
  } else {
    t7 = $[15];
    t8 = $[16];
  }
  useEffect(t7, t8);
  const userIsSeller = user?.id === item.advertiser.userId;
  const adType = item.type.key as AdTypes;
  let t9;
  if ($[17] !== item.advertiser.type) {
    t9 = isPro(item.advertiser.type);
    $[17] = item.advertiser.type;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const isProfessional = t9;
  let t10;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = [ownClasses["ad-info__listing-time"]];
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== item.date) {
    t11 = <ItemInsertionDate date={item.date} classes={t10} />;
    $[20] = item.date;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== FavoriteCounter || $[23] !== t11) {
    t12 = <div className={ownClasses["ad-info__listing-info"]}>{t11}{FavoriteCounter}</div>;
    $[22] = FavoriteCounter;
    $[23] = t11;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = classNames("caption", "book", ownClasses["ad-info__id"]);
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== item.urn) {
    t14 = parseId(item.urn);
    $[26] = item.urn;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  const t15 = `ID: ${t14}`;
  let t16;
  if ($[28] !== t15) {
    t16 = <span className={t13}>{t15}</span>;
    $[28] = t15;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== t12 || $[31] !== t16) {
    t17 = <div className={ownClasses["ad-info__top-caption-wrapper"]}>{t12}{t16}</div>;
    $[30] = t12;
    $[31] = t16;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = classNames("headline-5", ownClasses.title);
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] !== item.subject) {
    t19 = <h1 className={t18}>{item.subject}</h1>;
    $[34] = item.subject;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] !== item.geo) {
    t20 = <Location geoData={item.geo} />;
    $[36] = item.geo;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[38] !== adType || $[39] !== isAdEligibleFor3in1 || $[40] !== isShippableAd || $[41] !== isSold || $[42] !== parsedPrice) {
    t21 = (adType === AdTypes.GiveAway || !!parsedPrice || isShippableAd) && <><ItemPrice adType={adType} price={parsedPrice ?? 0} classes={[ownClasses.price]} size="large" badgeConfiguration={{
        display: "detail",
        shippable: isShippableAd,
        isItemSold: isSold
      }} />{isAdEligibleFor3in1 && <section className={ownClasses["paypal-section"]}><PayPalMessage amount={parsedPrice} /></section>}</>;
    $[38] = adType;
    $[39] = isAdEligibleFor3in1;
    $[40] = isShippableAd;
    $[41] = isSold;
    $[42] = parsedPrice;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  let t22;
  if ($[44] !== isDesktop || $[45] !== specialCampaignBannerData) {
    t22 = !isDesktop && specialCampaignBannerData ? <div className={ownClasses["campaign-banner-container"]}><SpecialCampaignBanner {...specialCampaignBannerData} /></div> : null;
    $[44] = isDesktop;
    $[45] = specialCampaignBannerData;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  let t23;
  if ($[47] !== isBuyNowVisibleMemo) {
    t23 = isBuyNowVisibleMemo && <div className={`sticky-cta-top-anchor ${ownClasses.buyButton}`}><BuyNowButton onClick={openBuyNowFlow} /></div>;
    $[47] = isBuyNowVisibleMemo;
    $[48] = t23;
  } else {
    t23 = $[48];
  }
  let t24;
  if ($[49] !== isSendOfferVisibleMemo) {
    t24 = isSendOfferVisibleMemo && <div className={`sticky-cta-top-anchor ${ownClasses.buyButton}`}><SendOfferButton onClick={openSendOfferFlow} /></div>;
    $[49] = isSendOfferVisibleMemo;
    $[50] = t24;
  } else {
    t24 = $[50];
  }
  let t25;
  if ($[51] !== item.urn || $[52] !== showFace2Face || $[53] !== showPaypal || $[54] !== user) {
    t25 = showFace2Face && user && <Face2FaceBox showPayPal={showPaypal} urn={item.urn} user={user} />;
    $[51] = item.urn;
    $[52] = showFace2Face;
    $[53] = showPaypal;
    $[54] = user;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  let t26;
  if ($[56] !== isProfessional || $[57] !== isShippableAd || $[58] !== isSold || $[59] !== shippingCosts || $[60] !== showPaypal) {
    t26 = isShippableAd && !isSold && shippingCosts && <InfoBox shippingCosts={shippingCosts} showPayPal={showPaypal} isPro={isProfessional} />;
    $[56] = isProfessional;
    $[57] = isShippableAd;
    $[58] = isSold;
    $[59] = shippingCosts;
    $[60] = showPaypal;
    $[61] = t26;
  } else {
    t26 = $[61];
  }
  let t27;
  if ($[62] !== item) {
    t27 = shouldRenderCertyWidget(item) ? <CertyWidget /> : null;
    $[62] = item;
    $[63] = t27;
  } else {
    t27 = $[63];
  }
  let t28;
  if ($[64] !== isSold || $[65] !== transactionStatus || $[66] !== userIsSeller) {
    t28 = isSold && !userIsSeller ? <Notice classes={[ownClasses["sold-box"]]} size="medium" variant="error" icon="TriangleExclamation">{statusMessageMap.get(transactionStatus) ?? "L'oggetto \xE8 stato gi\xE0 venduto"}</Notice> : null;
    $[64] = isSold;
    $[65] = transactionStatus;
    $[66] = userIsSeller;
    $[67] = t28;
  } else {
    t28 = $[67];
  }
  let t29;
  if ($[68] !== ancillaryServicesEnabled || $[69] !== isMobile || $[70] !== item || $[71] !== noAdvToggle || $[72] !== optimizelyReady) {
    t29 = optimizelyReady && noAdvToggle !== "B" && ancillaryServicesEnabled && <div className={ownClasses["useful-links-wrapper"]}><Usefullinks item={item} pulseInstance={getOrCreatePulse()} remoteData={prepareRemoteDataAdDetail("BELOW_PRICE", item, isMobile)} /></div>;
    $[68] = ancillaryServicesEnabled;
    $[69] = isMobile;
    $[70] = item;
    $[71] = noAdvToggle;
    $[72] = optimizelyReady;
    $[73] = t29;
  } else {
    t29 = $[73];
  }
  let t30;
  if ($[74] !== t17 || $[75] !== t19 || $[76] !== t20 || $[77] !== t21 || $[78] !== t22 || $[79] !== t23 || $[80] !== t24 || $[81] !== t25 || $[82] !== t26 || $[83] !== t27 || $[84] !== t28 || $[85] !== t29) {
    t30 = <>{t17}{t19}{t20}{t21}{t22}{t23}{t24}{t25}{t26}{t27}{t28}{t29}</>;
    $[74] = t17;
    $[75] = t19;
    $[76] = t20;
    $[77] = t21;
    $[78] = t22;
    $[79] = t23;
    $[80] = t24;
    $[81] = t25;
    $[82] = t26;
    $[83] = t27;
    $[84] = t28;
    $[85] = t29;
    $[86] = t30;
  } else {
    t30 = $[86];
  }
  return t30;
};
AdInfo.displayName = 'AdInfo';
export default AdInfo;
function _temp() {
  window.dispatchEvent(new CustomEvent(AdBuyEvent.OpenCartDialog));
}
function _temp2() {
  window.dispatchEvent(new CustomEvent(AdBuyEvent.OpenOfferDialog));
}